/**
 * CustomDomainProvider — router-level wrapper for public routes
 * (`/delivery/:slug`, `/portal/:token`, `/video/:token`,
 * `/sign/:token`, `/book/:slug`). Reads the current custom domain
 * (if any) and applies the brand overrides:
 *
 *   • Primary + accent colors → CSS custom properties on the root
 *     element via inline style. Matches `brandToCSSVars` from the
 *     existing customDomain service so downstream Tailwind classes
 *     (bg-[--orbit-orange], text-[--lunar-teal], etc.) rebrand
 *     without touching component code.
 *
 *   • Font family → `document.body.style.fontFamily` — the surest
 *     way to affect every child without a full CSS refactor.
 *
 *   • Logo URL is exposed via context for headers that want to
 *     render the studio's mark instead of Orbit's.
 *
 * On Orbit-owned hosts (localhost, orbit.app, vercel previews) this
 * component is a no-op — children render exactly as before.
 */

import { createContext, useContext, useEffect, ReactNode } from 'react';
import { useCustomDomainBrand, type CustomDomainBrand } from '@/hooks/useCustomDomainBrand';
import { brandToCSSVars } from '@/services/customDomain';

interface CustomDomainContextValue {
  isCustomDomain: boolean;
  brand: CustomDomainBrand | null;
}

const CustomDomainContext = createContext<CustomDomainContextValue>({
  isCustomDomain: false,
  brand: null,
});

export function useCustomDomainContext(): CustomDomainContextValue {
  return useContext(CustomDomainContext);
}

interface CustomDomainProviderProps {
  children: ReactNode;
}

export function CustomDomainProvider({ children }: CustomDomainProviderProps) {
  const { isCustomDomain, brand } = useCustomDomainBrand();

  // Apply font family to the body so every text descendant inherits.
  // Restored on unmount / brand change so we don't leak studio A's
  // typography onto studio B's page after a client-side nav.
  useEffect(() => {
    if (!brand?.fontFamily) return;
    const prev = document.body.style.fontFamily;
    document.body.style.fontFamily = brand.fontFamily;
    return () => {
      document.body.style.fontFamily = prev;
    };
  }, [brand?.fontFamily]);

  // Map the PublicCustomDomain shape brandToCSSVars expects (it uses
  // the *_color / *_family field names). Reconstruct that shape from
  // our normalized brand slice. Wave 6.1 added brand_bg_color +
  // brand_fg_color to the interface; wire those through so the studio's
  // chosen background / foreground actually paint on portal pages.
  const cssVars = brand
    ? brandToCSSVars({
        hostname: window.location.hostname,
        org_id: brand.orgId ?? '',
        brand_logo_url: brand.logoUrl,
        brand_primary_color: brand.primaryColor,
        brand_accent_color: brand.accentColor,
        brand_bg_color: brand.bgColor,
        brand_fg_color: brand.fgColor,
        brand_font_family: brand.fontFamily,
        hide_orbit_footer: brand.hideOrbitFooter,
      })
    : {};

  return (
    <CustomDomainContext.Provider value={{ isCustomDomain, brand }}>
      <div style={cssVars}>{children}</div>
    </CustomDomainContext.Provider>
  );
}

export default CustomDomainProvider;
